import React, { ComponentProps } from 'react';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { DefaultView, DeleteAction, OptionalEditView, Views } from '../../types/BlockWithViews';
import { ConfigurationAggregateEditView } from './views/ConfigurationAggregateEditView';
import { ConfigurationAggregateDefaultView } from './views/ConfigurationAggregateDefaultView';
import { Hint } from '../../_lib/components/Hint';
import { useSonTranslation } from '../../translation/useSonTranslation';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { isNotNullOrUndefined } from '../../lib/typeGuards';

type ConfigurationAggregateProps = Views<
  DefaultView<ConfigurationAggregateDefaultView> & OptionalEditView<ConfigurationAggregateEditView> & DeleteAction
>;

function ConfigurationAggregate({ views, activeView }: ConfigurationAggregateProps) {
  const { t } = useSonTranslation('ConfigurationAggregate');
  const { uuid, elementType } = useBlockContext();
  const { refreshCount } = useRefreshBlock();

  const buttons = [
    views.delete &&
    createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.ConfigurationAggregate}:erase`,
      DISPLAY_TYPE.MODAL,
      null,
    ),
    views.edit &&
    createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.ConfigurationAggregate}:edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <ConfigurationAggregateEditView {...views.edit} />,
    ),
  ]
    .filter(isNotNullOrUndefined)
    .reduce((btn, elem) => ({ ...elem, ...btn }), {});

  return (
    <>
      {views.edit && (
        <div className="configuration-file-upload__hint">
          <Hint message={t('hint')} />
        </div>
      )}
      <BlockInteractions
        key={refreshCount}
        headline={{
          text: t('headline'),
          visible: true,
          hierarchy: 3,
        }}
        alwaysShowInfo
        views={views}
        activeView={activeView}
        buttons={buttons}
        uuid={uuid}
        elementType={elementType}
      >
        <ConfigurationAggregateDefaultView {...views.default} />
      </BlockInteractions>
    </>
  );
}

export const ConfigurationAggregateBlock = asFetchableBlock(ConfigurationAggregate, BlockType.configurationaggregate);
export type ConfigurationAggregateBlock = ComponentProps<typeof ConfigurationAggregateBlock>;
